import { useCallback, useEffect, useRef, useState } from "react";

export type Suggestion = {
  placeId: string;
  description: string;
};

export type UseAddressAutocomplete = {
  suggestions: Suggestion[];
  loading: boolean;
  search: (input: string) => void;
  clear: () => void;
  resetSessionToken: () => void;
};

const DEBOUNCE_MS = 300;

export function useAddressAutocomplete(): UseAddressAutocomplete {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  function getSessionToken() {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }

  const clear = useCallback(() => {
    setSuggestions([]);
    setLoading(false);
  }, []);

  const search = useCallback((input: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!input.trim()) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const currentRequestId = ++requestIdRef.current;

    debounceRef.current = setTimeout(async () => {
      try {
        const response =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              input,
              sessionToken: getSessionToken(),
            }
          );

        if (currentRequestId !== requestIdRef.current) return;

        const mapped: Suggestion[] = response.suggestions
          .filter(
            (s): s is typeof s & { placePrediction: NonNullable<typeof s.placePrediction> } =>
              s.placePrediction !== null && s.placePrediction !== undefined
          )
          .map((s) => ({
            placeId: s.placePrediction.placeId,
            description: s.placePrediction.text.text,
          }));

        setSuggestions(mapped);
      } catch {
        if (currentRequestId === requestIdRef.current) {
          setSuggestions([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  return {
    suggestions,
    loading,
    search,
    clear,
    resetSessionToken,
  };
}
